import React from 'react';
import { string, bool } from 'prop-types';
import { propTypes } from '../../util/types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm, Field } from 'react-final-form';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldSelect,
  ImageFromFile,
  Icon,
} from '../../components';
import * as validators from '../../util/validators';
import classNames from 'classnames';
import { getMediaUrl } from '../../util/uploadedMedia';

import css from './OrgProfileEditForm.module.css';

const ACCEPT_IMAGES = 'image/*';
const BIO_MIN_LENGTH = 200;
const BIO_MAX_LENGTH = 700;

const yearsOptions = Array.from({ length: new Date().getFullYear() - 1979 }, (_, i) => ({
  key: (new Date().getFullYear() - i).toString(),
  label: (new Date().getFullYear() - i).toString(),
}));

/**
 * Single-organization edit form. Mirrors NewOrganizationForm fields/validators
 * but drops the create-specific chrome (card heading, error message, etc.) so
 * it sits cleanly inside the new SettingsAccordion on OrgSettingsProfilePage.
 *
 * Footer is a single Save button — no View / Delete / Cancel.
 */
const OrgProfileEditFormComponent = props => (
  <FinalForm
    {...props}
    // A refresh of the user record hands the form a new initialValues object.
    // Without this the form reinitialises and drops the picture just chosen.
    keepDirtyOnReinitialize
    render={fieldRenderProps => {
      const {
        className,
        rootClassName,
        handleSubmit,
        intl,
        form,
        invalid,
        pristine,
        values,
        saveInProgress,
        uploadImageInProgress,
        uploadImageError,
        saveError,
      } = fieldRenderProps;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = saveInProgress || uploadImageInProgress;
      const submitDisabled = invalid || pristine || submitInProgress;

      const nameLabel = intl.formatMessage({ id: 'OrganizationSettingsForm.nameLabel' });
      const namePlaceholder = intl.formatMessage({
        id: 'OrganizationSettingsForm.namePlaceholder',
      });
      const nameRequired = validators.required(
        intl.formatMessage({ id: 'OrganizationSettingsForm.nameRequired' })
      );

      const websiteLabel = intl.formatMessage({
        id: 'OrganizationSettingsForm.websiteLabel',
      });
      const websitePlaceholder = intl.formatMessage({
        id: 'OrganizationSettingsForm.websitePlaceholder',
      });
      const websiteValid = validators.validURL(
        intl.formatMessage({ id: 'OrganizationSettingsPage.websiteValid' })
      );

      const bioLabel = intl.formatMessage({ id: 'OrganizationSettingsForm.bioLabel' });
      const bioPlaceholder = intl.formatMessage({
        id: 'OrganizationSettingsForm.bioPlaceholder',
      });
      const bioRequired = validators.required(
        intl.formatMessage({ id: 'OrganizationSettingsForm.bioRequired' })
      );
      const bioMinLength = validators.minLength(
        intl.formatMessage(
          { id: 'OrganizationSettingsForm.bioMinLength' },
          { minLength: BIO_MIN_LENGTH }
        ),
        BIO_MIN_LENGTH
      );
      const bioMaxLength = validators.maxLength(
        intl.formatMessage(
          { id: 'OrganizationSettingsForm.bioMaxLength' },
          { maxLength: BIO_MAX_LENGTH }
        ),
        BIO_MAX_LENGTH
      );

      const foundedLabel = intl.formatMessage({
        id: 'OrganizationSettingsForm.foundedLabel',
      });
      const foundedPlaceholder = intl.formatMessage({
        id: 'OrganizationSettingsForm.foundedPlaceholder',
      });

      const founderLabel = intl.formatMessage({
        id: 'OrganizationSettingsForm.founderLabel',
      });
      const founderPlaceholder = intl.formatMessage({
        id: 'OrganizationSettingsForm.founderPlaceholder',
      });

      const instagramLinkMessage = intl.formatMessage({
        id: 'OrganizationSettingsForm.instagramLink',
      });
      const instagramLinkPlaceholderMessage = intl.formatMessage({
        id: 'OrganizationSettingsForm.instagramLinkPlaceholder',
      });
      const instagramLinkValidMessage = intl.formatMessage({
        id: 'OrganizationSettingsForm.instagramLinkValidMessage',
      });

      const profileImage = values?.profileImage;

      const imageFromFile =
        profileImage && profileImage.lastModified ? (
          <ImageFromFile
            id={profileImage.name}
            rootClassName={css.uploadedImage}
            aspectRatioClassName={css.squareAspectRatio}
            file={profileImage}
          />
        ) : null;

      const avatarComponent =
        profileImage && profileImage?.id ? (
          <div className={css.uploadedImage}>
            <div className={css.squareAspectRatio}>
              <img
                className={css.rootForImage}
                src={getMediaUrl(profileImage, 'card')}
                alt={profileImage.name}
              />
            </div>
          </div>
        ) : null;

      const chooseAvatarLabel =
        imageFromFile || avatarComponent ? (
          <div className={css.avatarContainer}>
            {imageFromFile}
            {avatarComponent}
            <div className={css.changeAvatar}>
              <Icon name="edit" size={14} className={css.changeAvatarIcon} />
              <FormattedMessage id="ProfileImageForm.changeAvatar" />
            </div>
          </div>
        ) : (
          <div className={css.avatarPlaceholder}>
            <div className={css.avatarPlaceholderText}>
              <FormattedMessage id="ProfileImageForm.addYourProfilePicture" />
            </div>
          </div>
        );

      const uploadErrorMessage = uploadImageError ? (
        <p className={css.error}>
          <FormattedMessage id="OrgProfileEditForm.uploadImageFailed" />
        </p>
      ) : null;

      const errorMessage = saveError ? (
        <p className={css.error}>
          <FormattedMessage id="OrgProfileEditForm.saveError" />
        </p>
      ) : null;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.fieldList}>
            <Field
              accept={ACCEPT_IMAGES}
              id="profileImage"
              name="profileImage"
              label={chooseAvatarLabel}
              type="file"
              form={null}
              disabled={false}
            >
              {fieldProps => {
                const { accept, id, input, label, disabled } = fieldProps;
                const { name, type } = input;
                const onChange = async e => {
                  const file = e.target.files[0];
                  if (file) {
                    form.change('profileImage', file);
                    form.blur('profileImage');
                  }
                };
                return (
                  <div className={css.imageUploadWrapper}>
                    <label htmlFor={id}>{label}</label>
                    <input
                      accept={accept}
                      id={id}
                      name={name}
                      className={css.imageInput}
                      disabled={disabled}
                      onChange={onChange}
                      type={type}
                    />
                  </div>
                );
              }}
            </Field>

            <FieldTextInput
              className={css.field}
              type="text"
              id="name"
              name="name"
              label={nameLabel}
              placeholder={namePlaceholder}
              validate={nameRequired}
            />
            <FieldTextInput
              className={css.field}
              type="text"
              id="website"
              name="website"
              label={websiteLabel}
              placeholder={websitePlaceholder}
              validate={websiteValid}
            />
            <FieldSelect
              className={css.field}
              id="founded"
              name="founded"
              options={yearsOptions}
              label={foundedLabel}
              placeholder={foundedPlaceholder}
            />
            <FieldTextInput
              className={css.field}
              type="text"
              id="founder"
              name="founder"
              label={founderLabel}
              placeholder={founderPlaceholder}
            />
            <FieldTextInput
              className={css.field}
              type="text"
              id="instagramLink"
              name="instagramLink"
              label={instagramLinkMessage}
              placeholder={instagramLinkPlaceholderMessage}
              validate={validators.validInstagramLink(instagramLinkValidMessage)}
            />
            <FieldTextInput
              className={css.field}
              type="textarea"
              id="bio"
              name="bio"
              label={bioLabel}
              placeholder={bioPlaceholder}
              validate={validators.composeValidators(bioRequired, bioMinLength, bioMaxLength)}
            />
          </div>

          {uploadErrorMessage}
          {errorMessage}

          <div className={css.footer}>
            <PrimaryButton
              className={css.submitButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
            >
              <FormattedMessage id="OrgProfileEditForm.save" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

OrgProfileEditFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
  saveInProgress: false,
  uploadImageInProgress: false,
  uploadImageError: null,
  saveError: null,
};

OrgProfileEditFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  saveInProgress: bool,
  uploadImageInProgress: bool,
  uploadImageError: propTypes.error,
  saveError: propTypes.error,
};

const OrgProfileEditForm = compose(injectIntl)(OrgProfileEditFormComponent);

export default OrgProfileEditForm;
